import { useEffect, useState } from 'react';
import type { Task } from '../types/task';
import type { Workstream } from '../types/workstream';

interface TaskRunExplainerProps {
  task: Task;
  workstreamID: string;
  workstream: Workstream | null;
}

function formatTaskRef(t: Task, ws: Workstream | null): string {
  const num = t.TaskNumber;
  const name = t.Title;
  if (!num) return name;
  if (ws?.defaultShortName) {
    return `${name} (${ws.defaultShortName.toUpperCase()}-${num})`;
  }
  return `${name} (${num})`;
}

export default function TaskRunExplainer({ task, workstreamID, workstream }: TaskRunExplainerProps) {
  const [blockingTask, setBlockingTask] = useState<Task | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setBlockingTask(null);
    setLoaded(false);

    if (task.Parallel) {
      setLoaded(true);
      return;
    }

    // Fetch just the immediate predecessor using beforeTaskId.
    fetch(`/api/workstreams/${encodeURIComponent(workstreamID)}/tasks?beforeTaskId=${encodeURIComponent(task.TaskID)}&maxResults=1`)
      .then(async (resp) => {
        if (cancelled || !resp.ok) return;
        const data: { Tasks: Task[] } = await resp.json();
        const tasks = data.Tasks ?? [];
        if (!cancelled && tasks.length > 0) {
          const prev = tasks[0];
          if (prev.State !== 'Completed') {
            setBlockingTask(prev);
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });

    return () => { cancelled = true; };
  }, [task.TaskID, task.Parallel, workstreamID]);

  if (!loaded) return null;

  // Determine the reason the task can't run. Check hard blockers
  // (missing env/spec) first since those prevent execution even if
  // the predecessor completes.
  let message: React.ReactNode = null;

  if (!task.EnvironmentID) {
    message = 'You must select an environment before Plan 42 can run this task.';
  } else if (!task.Prompt?.trim()) {
    message = 'You must add a task spec before Plan 42 can run this task.';
  } else if (blockingTask) {
    const ref = formatTaskRef(blockingTask, workstream);
    message = (
      <>
        Plan 42 will automatically run this task once{' '}
        <span style={{
          display: 'inline-block',
          padding: '1px 8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontWeight: 500,
        }}>
          {ref}
        </span>
        {' '}is completed.
      </>
    );
  }

  if (!message) return null;

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '10px 16px',
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#24292f',
        lineHeight: 1.6,
      }}
    >
      {message}
    </div>
  );
}
